import { useEffect, useState } from "react";
import { adminApi, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Plus, Pencil, Key, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const PERMISSIONS = [
  { id: "events", label: "Events" },
  { id: "specials", label: "Specials" },
  { id: "reservations", label: "Reservations" },
  { id: "applications", label: "Applications" },
  { id: "content", label: "Site Content" },
  { id: "reviews", label: "Reviews" },
];

const EMPTY = { email: "", name: "", password: "", permissions: PERMISSIONS.map((p) => p.id) };

export default function UsersManager() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // { mode: "create" | "edit" | "reset", user }
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
  }, []);

  function startCreate() {
    setForm(EMPTY);
    setDialog({ mode: "create" });
  }
  function startEdit(u) {
    setForm({ email: u.email, name: u.name, password: "", permissions: u.permissions || [] });
    setDialog({ mode: "edit", user: u });
  }
  function startReset(u) {
    setForm({ ...EMPTY, password: "" });
    setDialog({ mode: "reset", user: u });
  }

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      if (dialog.mode === "create") {
        await adminApi.post("/admin/users", form);
        toast.success(`Created ${form.email}`);
      } else if (dialog.mode === "edit") {
        await adminApi.patch(`/admin/users/${dialog.user.id}`, {
          name: form.name,
          permissions: form.permissions,
        });
        toast.success("Updated");
      } else if (dialog.mode === "reset") {
        await adminApi.post(`/admin/users/${dialog.user.id}/reset-password`, {
          password: form.password,
        });
        toast.success("Password reset");
      }
      setDialog(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  }

  async function remove(u) {
    if (!window.confirm(`Delete ${u.email}?`)) return;
    try {
      await adminApi.delete(`/admin/users/${u.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  const togglePerm = (id) =>
    setForm((p) => ({
      ...p,
      permissions: p.permissions.includes(id)
        ? p.permissions.filter((x) => x !== id)
        : [...p.permissions, id],
    }));

  return (
    <div data-testid="users-manager" className="space-y-6">
      <div className="flex justify-between items-center">
        <p className="text-sm text-[var(--trio-muted)] max-w-xl">
          Add or remove team admins. Toggle exactly which sections they can
          access — Reservations, Events, Specials, Applications, Site Content.
        </p>
        <button
          onClick={startCreate}
          data-testid="user-new-btn"
          className="inline-flex items-center gap-2 bg-[var(--trio-primary)] hover:bg-[var(--trio-primary-hover)] text-white px-5 py-2.5 text-xs tracking-widest uppercase font-bold transition-colors"
        >
          <Plus className="w-4 h-4" /> New Admin
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-[var(--trio-muted)]">Loading…</div>
      ) : (
        <div className="space-y-3">
          {users.map((u) => {
            const isOwner = u.role === "owner";
            const isSelf = me?.id === u.id;
            return (
              <article
                key={u.id}
                data-testid={`user-row-${u.id}`}
                className="bg-[var(--trio-surface)] border border-[var(--trio-border)] p-5 grid grid-cols-1 md:grid-cols-12 gap-4 items-center"
              >
                <div className="md:col-span-4">
                  <div className="flex items-center gap-2">
                    {isOwner && (
                      <ShieldCheck className="w-4 h-4 text-[var(--trio-accent)]" />
                    )}
                    <div className="font-display uppercase text-lg tracking-tight">
                      {u.name}
                    </div>
                    {isSelf && (
                      <span className="text-[9px] uppercase tracking-widest text-[var(--trio-muted)]">
                        (you)
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-[var(--trio-muted)]">{u.email}</div>
                </div>
                <div className="md:col-span-5">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--trio-muted)] font-bold mb-1.5">
                    Access
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {isOwner ? (
                      <span className="text-xs uppercase tracking-widest border border-[var(--trio-accent)] text-[var(--trio-accent)] px-2 py-0.5">
                        Full · Owner
                      </span>
                    ) : (
                      PERMISSIONS.map((p) => {
                        const has = (u.permissions || []).includes(p.id);
                        return (
                          <span
                            key={p.id}
                            className={`text-[10px] uppercase tracking-widest px-2 py-0.5 border ${
                              has
                                ? "bg-[var(--trio-text)] text-[var(--trio-bg)] border-[var(--trio-text)]"
                                : "border-[var(--trio-border)] text-[var(--trio-muted)] opacity-50"
                            }`}
                          >
                            {p.label}
                          </span>
                        );
                      })
                    )}
                  </div>
                </div>
                <div className="md:col-span-3 flex justify-end gap-2">
                  {!isOwner && (
                    <>
                      <IconBtn
                        title="Edit"
                        testid={`user-edit-${u.id}`}
                        onClick={() => startEdit(u)}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </IconBtn>
                      <IconBtn
                        title="Reset password"
                        testid={`user-reset-${u.id}`}
                        onClick={() => startReset(u)}
                      >
                        <Key className="w-3.5 h-3.5" />
                      </IconBtn>
                      <IconBtn
                        title="Delete"
                        testid={`user-delete-${u.id}`}
                        onClick={() => remove(u)}
                      >
                        <Trash2 className="w-3.5 h-3.5 text-red-700" />
                      </IconBtn>
                    </>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent
          data-testid="user-form-dialog"
          className="bg-[var(--trio-bg)] border-[var(--trio-border)] rounded-none max-w-lg"
        >
          <DialogHeader>
            <DialogTitle className="font-display uppercase text-2xl tracking-tight">
              {dialog?.mode === "create"
                ? "New Admin"
                : dialog?.mode === "edit"
                ? `Edit ${dialog?.user?.name}`
                : `Reset password · ${dialog?.user?.name}`}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            {dialog?.mode === "create" && (
              <>
                <Field label="Full Name" value={form.name} onChange={(v) => setForm((p) => ({ ...p, name: v }))} required testid="user-name" />
                <Field label="Email" type="email" value={form.email} onChange={(v) => setForm((p) => ({ ...p, email: v }))} required testid="user-email" />
                <Field label="Starter Password" type="password" value={form.password} onChange={(v) => setForm((p) => ({ ...p, password: v }))} required testid="user-password" />
              </>
            )}
            {dialog?.mode === "edit" && (
              <Field label="Full Name" value={form.name} onChange={(v) => setForm((p) => ({ ...p, name: v }))} required testid="user-name" />
            )}
            {dialog?.mode === "reset" && (
              <Field label="New Password" type="password" value={form.password} onChange={(v) => setForm((p) => ({ ...p, password: v }))} required testid="user-password" />
            )}

            {(dialog?.mode === "create" || dialog?.mode === "edit") && (
              <div>
                <Label className="text-xs tracking-widest uppercase">Permissions</Label>
                <div className="mt-3 space-y-2">
                  {PERMISSIONS.map((p) => (
                    <label
                      key={p.id}
                      className="flex items-center justify-between border border-[var(--trio-border)] px-3 py-2 cursor-pointer"
                    >
                      <span className="text-sm uppercase tracking-widest">
                        {p.label}
                      </span>
                      <Switch
                        checked={form.permissions.includes(p.id)}
                        onCheckedChange={() => togglePerm(p.id)}
                        data-testid={`perm-${p.id}`}
                      />
                    </label>
                  ))}
                </div>
              </div>
            )}
            <button
              type="submit"
              disabled={saving}
              data-testid="user-save"
              className="w-full bg-[var(--trio-primary)] hover:bg-[var(--trio-primary-hover)] text-white py-3 text-xs tracking-widest uppercase font-bold disabled:opacity-60 transition-colors"
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <Label className="text-xs tracking-widest uppercase">{label}</Label>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
        className="mt-2 rounded-none border-[var(--trio-border)] bg-[var(--trio-bg)]"
      />
    </div>
  );
}

function IconBtn({ children, onClick, title, testid }) {
  return (
    <button
      onClick={onClick}
      title={title}
      data-testid={testid}
      type="button"
      className="p-2 border border-[var(--trio-border)] hover:bg-[var(--trio-bg)] transition-colors"
    >
      {children}
    </button>
  );
}
